import { useState } from "react";
import {
  Box, Typography, Card, CardContent, CardActions, Button, Chip, Stack, Alert, Divider,
  CircularProgress, Checkbox, FormControlLabel, Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { itemsApi, type SubstituteOptionDto } from "@/api/endpoints";
import { GenericInfoButton } from "@/components/GenericInfoButton";

interface SubstitutePanelProps {
  primaryItemId: string;
  primaryItemName: string;
  warehouseId: string;
  requiredQty: number;
  /** `confirmed` is the pharmacist's acceptance, required when the mapping demands it. */
  onSelect: (sub: SubstituteOptionDto, confirmed: boolean) => void;
  onClose: () => void;
}

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * Dispensable stock, with the shelf count behind it.
 *
 * The headline number excludes expired and already-reserved stock, so it can be well below what a
 * stock report shows. Spelling out why avoids the "the system says 22 but we have 30 boxes"
 * conversation at the counter.
 */
function StockBreakdown({ s, hasStock }: { s: SubstituteOptionDto; hasStock: boolean }) {
  const total = s.totalQty ?? s.availableQty;
  const expired = s.expiredQty ?? 0;
  const reserved = s.reservedQty ?? 0;
  const hasDeductions = expired > 0 || reserved > 0;

  const detail = hasDeductions
    ? [
        `Total on shelf ${total}`,
        expired > 0 ? `expired ${expired}` : null,
        reserved > 0 ? `reserved for other bills ${reserved}` : null,
        `usable ${s.availableQty}`,
      ].filter(Boolean).join(" · ")
    : `${s.availableQty} usable`;

  return (
    <Tooltip title={detail}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
          size="small"
          color={hasStock ? "success" : "warning"}
          label={hasStock ? `${s.availableQty} usable` : `Only ${s.availableQty} usable`}
        />
        {hasDeductions && (
          <Typography variant="caption" color="text.secondary">
            of {total}
            {expired > 0 && ` · ${expired} expired`}
            {reserved > 0 && ` · ${reserved} reserved`}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

const REASON_LABEL: Record<string, string> = {
  OutOfStock: "Out of stock",
  CheaperEquivalent: "Cheaper equivalent",
  PatientPreference: "Patient preference",
  Therapeutic: "Therapeutic substitute",
  Generic: "Generic equivalent",
};

export function SubstitutePanel({
  primaryItemId, primaryItemName, warehouseId, requiredQty, onSelect, onClose,
}: SubstitutePanelProps) {
  const query = useQuery({
    queryKey: ["substitutes", primaryItemId, warehouseId],
    queryFn: () => itemsApi.substitutes(primaryItemId, warehouseId),
    enabled: !!primaryItemId && !!warehouseId,
  });

  // Per-candidate, because the pharmacist may weigh up several before picking one.
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});

  return (
    <Box
      sx={{ p: 2, borderLeft: 1, borderColor: "divider", height: "100%", overflowY: "auto" }}
      data-testid="billing-substitute-panel"
      data-primary-item-id={primaryItemId}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SwapHorizIcon color="warning" />
          <Typography variant="h6">Substitute options</Typography>
        </Stack>
        <Button size="small" startIcon={<CloseIcon fontSize="small" />} onClick={onClose} data-testid="billing-substitute-close">Close</Button>
      </Stack>

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        <strong>{primaryItemName}</strong> needs {requiredQty} unit{requiredQty === 1 ? "" : "s"}.
        Choose a substitute below.
      </Alert>

      {query.isLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
      {query.isError && <Alert severity="error">Could not load substitutes.</Alert>}
      {query.data?.length === 0 && (
        <Alert severity="info">No substitutes configured for this item.</Alert>
      )}

      <Stack spacing={1.5}>
        {query.data?.map((s) => {
          const hasStock = s.availableQty >= requiredQty;
          return (
            <Card
              key={s.itemId}
              variant="outlined"
              sx={{ borderColor: hasStock ? "success.light" : "warning.light" }}
              data-testid={`billing-substitute-card-${s.sku}`}
              data-substitute-item-id={s.itemId}
              data-substitute-available={s.availableQty}
            >
              <CardContent sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2">{s.sku} — {s.name}</Typography>
                    {s.genericName && (
                      <Typography variant="caption" color="text.secondary">{s.genericName}</Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {/* Read the generic's monograph before swapping — this is the moment it matters. */}
                    <GenericInfoButton genericNameId={s.genericNameId} genericName={s.genericName} />
                    <Chip size="small" label={`Priority ${s.priority}`} variant="outlined" />
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap" }}>
                  {s.form && <Chip size="small" label={s.form} variant="outlined" />}
                  {s.strength && <Chip size="small" label={s.strength} variant="outlined" />}
                  {s.manufacturer && <Chip size="small" label={s.manufacturer} variant="outlined" />}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {REASON_LABEL[s.reasonCode] ?? s.reasonCode}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {s.mrp != null && <Typography variant="caption">MRP {inr.format(s.mrp)}</Typography>}
                    <StockBreakdown s={s} hasStock={hasStock} />
                  </Stack>
                </Stack>
                {s.requiresPharmacistConfirm && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      Nothing verifies that these two drugs are equivalent, and the quantity is not
                      adjusted for strength. Confirming records you as accepting the swap.
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!confirmed[s.itemId]}
                          onChange={(_, v) => setConfirmed(c => ({ ...c, [s.itemId]: v }))}
                          data-testid={`billing-substitute-confirm-${s.sku}`}
                        />
                      }
                      label="I confirm this substitution"
                    />
                  </Alert>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button
                  size="small" variant="contained"
                  // The API rejects an unconfirmed swap where the mapping demands one; blocking
                  // here means the pharmacist sees why instead of a validation error on save.
                  onClick={() => onSelect(s, !!confirmed[s.itemId])}
                  disabled={s.availableQty === 0
                    || (s.requiresPharmacistConfirm && !confirmed[s.itemId])}
                  data-testid={`billing-substitute-use-${s.sku}`}
                >
                  Use this substitute
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
